import {
  Connection,
  PublicKey,
  Transaction,
  ComputeBudgetProgram,
  TransactionInstruction,
  Keypair,
  sendAndConfirmTransaction,
} from "@solana/web3.js";
import { AnchorProvider } from "@coral-xyz/anchor";
import { BondingCurveProgramClient, TokenProgramClient } from "./anchor/program-client";
import { findPoolPDA, findPoolTokenAccountPDA, PROGRAM_IDS } from "./anchor/idl-types";
import { logger } from "./logger";

// Helper function to sign and send transaction
export async function signAndSendTransaction(
  connection: Connection,
  transaction: Transaction,
  wallet: {
    publicKey: PublicKey;
    signTransaction: (tx: Transaction) => Promise<Transaction>;
  }
): Promise<string> {
  const timerId = logger.startTimer('sign_and_send_transaction', {
    feePayer: transaction.feePayer?.toString(),
    instructions: transaction.instructions.length,
  });

  try {
    // Sign the transaction
    const signedTransaction = await wallet.signTransaction(transaction);
    
    // Send and confirm the transaction
    const signature = await logger.withTimer('send_transaction', async () => {
      return await sendAndConfirmTransaction(
        connection,
        signedTransaction,
        { commitment: 'confirmed' }
      );
    });

    logger.endTimer(timerId, {
      signature,
      transactionSize: signedTransaction.serialize().length,
    });

    return signature;
  } catch (error) {
    logger.endTimer(timerId, { error: error instanceof Error ? error.message : String(error) });
    logger.error('Failed to sign and send transaction', {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

export interface BuildBondingCurveTradeTransactionParams {
  coinId: string; // Token mint address
  tradeType: "buy" | "sell";
  amount: bigint; // Amount in lamports for buy, token amount for sell
  userWallet: PublicKey;
  tokenMintAddress: PublicKey;
  slippageTolerance: number; // Percentage (e.g., 0.5 for 0.5%)
  priorityFee?: bigint; // In micro-lamports
}

export interface BuildTokenCreationTransactionParams {
  name: string;
  symbol: string;
  uri: string; // Metadata URI (IPFS or Arweave)
  decimals: number; // Default 9
  totalSupply: bigint; // Default 1B * 10^9
  targetMarketCap: bigint; // In lamports
  creator: PublicKey;
  priorityFee?: bigint;
}

export async function buildBondingCurveTradeTransaction(
  connection: Connection,
  wallet: {
    publicKey: PublicKey;
    signTransaction: (tx: Transaction) => Promise<Transaction>;
    signAllTransactions: (txs: Transaction[]) => Promise<Transaction[]>;
  },
  params: BuildBondingCurveTradeTransactionParams
): Promise<Transaction> {
  const timerId = logger.startTimer('build_bonding_curve_trade_transaction', {
    coinId: params.coinId,
    tradeType: params.tradeType,
    amount: params.amount.toString(),
    userWallet: params.userWallet.toString(),
  });

  try {
    logger.info('Building bonding curve trade transaction', {
      coinId: params.coinId,
      tradeType: params.tradeType,
      amount: params.amount.toString(),
      slippageTolerance: params.slippageTolerance,
    });

    const provider = new AnchorProvider(connection, wallet, {});
    const bondingCurveClient = new BondingCurveProgramClient(provider);

    // Get pool PDA
    const [poolPda] = findPoolPDA(params.tokenMintAddress, new PublicKey(PROGRAM_IDS.BONDING_CURVE));

    // Fetch pool account data using Anchor
    const pool = await logger.withTimer('fetch_pool_data', async () => {
      const poolData = await bondingCurveClient.getPoolData(params.tokenMintAddress);
      if (!poolData) {
        throw new Error("Pool not found");
      }
      return poolData;
    }, { tokenMintAddress: params.tokenMintAddress.toString() });

  // Calculate expected output
  let expectedOutput: number;
  let minOutput: bigint;

  if (params.tradeType === "buy") {
    const solAmount = Number(params.amount) / 1e9; // Convert lamports to SOL
    const result = bondingCurveClient.calculateBuyOutput(
      Number(pool.solReserve) / 1e9,
      Number(pool.tokenReserve) / 1e9,
      solAmount
    );
    expectedOutput = result.tokensOut;
    // Convert to smallest token units
    const expectedTokens = expectedOutput * 1e9;
    minOutput = BigInt(Math.floor(expectedTokens * (1 - params.slippageTolerance / 100)));
  } else {
    const tokenAmount = Number(params.amount); // Already in token units
    const result = bondingCurveClient.calculateSellOutput(
      Number(pool.solReserve) / 1e9,
      Number(pool.tokenReserve) / 1e9,
      tokenAmount / 1e9 // Convert to token units for calculation
    );
    expectedOutput = result.solOut;
    // Convert to lamports
    const expectedSol = expectedOutput * 1e9;
    minOutput = BigInt(Math.floor(expectedSol * (1 - params.slippageTolerance / 100)));
  }

  // Build the trade instruction
  let tradeInstruction: TransactionInstruction;
  if (params.tradeType === "buy") {
    tradeInstruction = await bondingCurveClient.buildBuyInstruction(
      params.userWallet,
      params.tokenMintAddress,
      params.amount,
      minOutput
    );
  } else {
    tradeInstruction = await bondingCurveClient.buildSellInstruction(
      params.userWallet,
      params.tokenMintAddress,
      params.amount,
      minOutput
    );
  }

  // Create transaction
  const transaction = new Transaction();

  // Add compute budget instruction if priority fee is provided
  if (params.priorityFee) {
    const computeBudgetIx = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: params.priorityFee,
    });
    transaction.add(computeBudgetIx);
  }

  // Add the trade instruction
  transaction.add(tradeInstruction);

  // Set recent blockhash and fee payer
  const { blockhash } = await logger.withTimer('get_latest_blockhash', async () => {
    return await connection.getLatestBlockhash();
  });

  transaction.recentBlockhash = blockhash;
  transaction.feePayer = params.userWallet;

  logger.endTimer(timerId, {
    transactionSize: transaction.serialize().length,
    instructionCount: transaction.instructions.length,
  });

  return transaction;
 } catch (error) {
   logger.endTimer(timerId, { error: error instanceof Error ? error.message : String(error) });
   logger.error('Failed to build bonding curve trade transaction', {
     coinId: params.coinId,
     tradeType: params.tradeType,
     error: error instanceof Error ? error.message : String(error),
   });
   throw error;
 }
}

export async function buildTokenCreationTransaction(
  connection: Connection,
  wallet: {
    publicKey: PublicKey;
    signTransaction: (tx: Transaction) => Promise<Transaction>;
    signAllTransactions: (txs: Transaction[]) => Promise<Transaction[]>;
  },
  params: BuildTokenCreationTransactionParams
): Promise<{ transaction: Transaction; mintKeypair: Keypair }> {
  // Validate parameters
  if (!params.name || params.name.length > 32) {
    throw new Error("Invalid token name: must be non-empty and <= 32 characters");
  }
  if (!params.symbol || params.symbol.length > 10) {
    throw new Error("Invalid token symbol: must be non-empty and <= 10 characters");
  }
  if (params.decimals < 0 || params.decimals > 9) {
    throw new Error("Invalid decimals: must be between 0 and 9");
  }
  if (params.totalSupply <= 0n) {
    throw new Error("Invalid total supply: must be positive");
  }
  if (params.targetMarketCap <= 0n) {
    throw new Error("Invalid target market cap: must be positive");
  }

  // Generate new mint keypair
  const mint = Keypair.generate();

  // Check if pool already exists
  const [poolPda] = findPoolPDA(mint.publicKey, new PublicKey(PROGRAM_IDS.BONDING_CURVE));
  try {
    const poolAccount = await connection.getAccountInfo(poolPda);
    if (poolAccount) {
      throw new Error("Pool already exists for this mint");
    }
  } catch (error) {
    if (error instanceof Error && !error.message.includes("Pool already exists")) {
      throw new Error(`RPC error checking pool existence: ${error.message}`);
    }
  }

  // Create Anchor provider
  const provider = new AnchorProvider(connection, wallet, {});

  // Instantiate clients
  const tokenClient = new TokenProgramClient(provider);
  const bondingCurveClient = new BondingCurveProgramClient(provider);

  // Get pool token account PDA
  const [poolTokenAccount] = findPoolTokenAccountPDA(poolPda, new PublicKey(PROGRAM_IDS.BONDING_CURVE));
  const INITIAL_TOKEN_RESERVE = 800_000_000_000_000_000n; // 800M tokens with 9 decimals

  // Build instructions
  try {
    const createTokenIx = await tokenClient.buildCreateTokenInstruction(
      params.creator,
      mint.publicKey,
      params.name,
      params.symbol,
      params.uri,
      params.decimals,
      params.totalSupply
    );

    const mintInitialSupplyIx = await tokenClient.buildMintInitialSupplyInstruction(
      params.creator,
      mint.publicKey,
      poolTokenAccount,
      INITIAL_TOKEN_RESERVE
    );

    const initializePoolIx = await bondingCurveClient.buildInitializePoolInstruction(
      params.creator,
      mint.publicKey,
      params.targetMarketCap
    );

    // Create transaction
    const transaction = new Transaction();

    // Add compute budget instruction if priority fee is provided
    if (params.priorityFee) {
      const computeBudgetIx = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: params.priorityFee,
      });
      transaction.add(computeBudgetIx);
    }

    // Add all instructions
    transaction.add(createTokenIx);
    transaction.add(mintInitialSupplyIx);
    transaction.add(initializePoolIx);

    // Set recent blockhash and fee payer
    const { blockhash } = await connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = params.creator;

    // Partial sign with mint keypair
    transaction.partialSign(mint);

    return { transaction, mintKeypair: mint };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to build token creation transaction: ${error.message}`);
    }
    throw new Error("Unknown error occurred while building token creation transaction");
  }
}